<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section with Slider -->
    <div class="relative h-screen overflow-hidden">
      <img
        :src="slides[currentSlide].image"
        class="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
        alt="Slide"
      />
      <div class="absolute inset-0 bg-black bg-opacity-50"></div>

      <div
        class="absolute inset-0 flex items-center justify-center text-white text-center px-4"
      >
        <div>
          <h1 class="text-5xl font-bold mb-4">
            {{ slides[currentSlide].title }}
          </h1>
          <p class="text-xl mb-8">{{ slides[currentSlide].description }}</p>
          <button
            @click="$router.push({ path: '/register' })"
            class="bg-yellow-600 hover:bg-yellow-700 px-8 py-3 rounded-full font-semibold flex items-center mx-auto"
          >
            Get Your School On BITSEMS Now

            <i data-lucide="arrow-right" class="ml-2"></i>
          </button>

          <v-btn
            rounded
            color="blue"
            dark
            class="mt-4"
            x-large
            @click="$router.push({ path: '/login' })"
          >
            Login to BITSEMS
          </v-btn>
        </div>
      </div>

      <!-- <button
        @click="prevSlide"
        class="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-30 p-2 rounded-full hover:bg-opacity-50"
      >
        <i data-lucide="chevron-left" class="text-white"></i>
      </button> -->
      <!-- <button
        @click="nextSlide"
        class="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-30 p-2 rounded-full hover:bg-opacity-50"
      >
        <i data-lucide="chevron-right" class="text-white"></i>
      </button> -->
    </div>

    <!-- Features Grid -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-16">
          Why Schools Choose BITSEMS
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="p-6 bg-gray-50 rounded-xl hover:shadow-lg feature-card"
          >
            <div class="text-blue-600 mb-4">
              <i :data-lucide="feature.icon" class="w-6 h-6"></i>
            </div>
            <h3 class="text-xl font-semibold mb-2">{{ feature.title }}</h3>
            <p class="text-gray-600">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="bg-blue-600 py-16">
      <div
        class="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center"
      >
        <div v-for="(stat, index) in stats" :key="index">
          <div class="text-4xl font-bold mb-2 stat-number">
            {{ stat.number }}
          </div>
          <div class="text-blue-100">{{ stat.label }}</div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-16">
          Powerful BITSEMS Tools
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            v-for="(service, index) in services"
            :key="index"
            class="bg-white p-8 rounded-xl shadow-lg"
          >
            <i
              :data-lucide="service.icon"
              class="text-blue-600 mb-4 w-8 h-8"
            ></i>
            <h3 class="text-xl font-semibold mb-4">{{ service.title }}</h3>
            <p class="text-gray-600 mb-4">{{ service.description }}</p>
            <button class="text-blue-600 font-semibold">Learn More →</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4">
        <h2 class="text-4xl font-bold text-center mb-16">
          Trusted by Schools Nationwide
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            v-for="(testimonial, i) in testimonials"
            :key="i"
            class="p-8 bg-gray-50 rounded-xl testimonial-card"
          >
            <div class="flex mb-4">
              <i
                data-lucide="star"
                class="w-5 h-5 text-yellow-400"
                v-for="i in 5"
                :key="i"
              ></i>
            </div>
            <p class="text-gray-600 mb-6">
              {{ testimonial.text }}
            </p>
            <div class="flex items-center">
              <!-- <img
                :src="`https://i.pravatar.cc/150?img=${n}`"
                class="w-12 h-12 rounded-full mr-4"
              /> -->
              <div>
                <div class="font-semibold">{{ testimonial.author }}</div>
                <div class="text-gray-500 text-sm">
                  {{ testimonial.title }} - {{ testimonial.school }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Newsletter Subscription -->
    <section class="bg-blue-600 py-20 text-white text-center">
      <div class="max-w-3xl mx-auto px-4">
        <h2 class="text-3xl font-bold mb-8">
          Get School Management Tips & Updates
        </h2>
        <form @submit.prevent="subscribe" class="flex gap-4 max-w-md mx-auto">
          <input
            v-model="subscriberEmail"
            type="email"
            placeholder="Enter your email"
            class="flex-1 px-4 py-2 rounded-lg text-black"
            required
          />
          <button
            class="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100"
          >
            Subscribe
          </button>
        </form>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-900 text-white py-12">
      <div class="max-w-7xl mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 class="text-xl font-bold mb-4">About BITSEMS</h3>
            <p class="text-gray-400">
              BITSEMS helps schools simplify finance, bulk SMS, and admin in one
              easy system.
            </p>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4">Quick Links</h3>
            <ul class="space-y-2 text-gray-400">
              <li><a href="#" class="hover:text-white">Home</a></li>
              <li><a href="#" class="hover:text-white">Features</a></li>
              <li><a href="#" class="hover:text-white">Register</a></li>
              <li><a href="#" class="hover:text-white">Contact</a></li>
            </ul>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4">Support</h3>
            <ul class="space-y-2 text-gray-400">
              <li class="flex items-center">
                <i data-lucide="phone" class="w-4 h-4 mr-2"></i> Call/WhatsApp
                Kenneth: 076 243 9441
              </li>
              <li class="flex items-center">
                <i data-lucide="globe" class="w-4 h-4 mr-2"></i> Visit:
                https://bitsems.co.za
              </li>
              <li class="flex items-center">
                <i data-lucide="mail" class="w-4 h-4 mr-2"></i>
                info@bitsems.co.za
              </li>
            </ul>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4">Stay Connected</h3>
            <div class="flex space-x-4">
              <a href="#" class="hover:text-blue-400"
                ><i data-lucide="facebook" class="w-6 h-6"></i
              ></a>
              <a href="#" class="hover:text-blue-400"
                ><i data-lucide="twitter" class="w-6 h-6"></i
              ></a>
              <a href="#" class="hover:text-blue-400"
                ><i data-lucide="instagram" class="w-6 h-6"></i
              ></a>
            </div>
          </div>
        </div>
        <div
          class="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400"
        >
          <p>© 2024 BITSEMS. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  data() {
    return {
      currentSlide: 0,
      subscriberEmail: "",
      interval: null,
      slides: [
        {
          title: "BITSEMS for Smart Schools",
          description:
            "Manage Finances, SMS Campaigns, Budgets & More – All in One Place",
          image:
            "https://api.traceytrustschool.co.za/images/bitsems/bits-front-page.jpeg",
        },
        {
          title: "AI-Powered Bulk SMS & Reports",
          description:
            "Communicate instantly with students and parents using BITSEMS AI tools",
          image:
            "https://api.traceytrustschool.co.za/images/bitsems/bitsems-dashboard.png",
        },
        {
          title: "All-in-One School System",
          description:
            "From finance to communication to reporting – BITSEMS handles it all.",
          image:
            "https://api.traceytrustschool.co.za/images/bitsems/bitsems-student-profile.png",
        },
      ],
      testimonials: [
        {
          text:
            "BITSEMS transformed how we handle school fees. Parents now receive instant updates, and monthly statements are generated automatically. It’s a game changer for finance teams.",
          author: "Mr. S. G. Mdunge",
          title: "Principal",
          school: "Masibumbane High School",
          site: "https://masibumbanehighschool.site",
        },
        {
          text:
            "We cut down on admin time by over 60% after switching to BITSEMS. From budgeting to sending SMS updates, everything is now streamlined and effortless.",
          author: "Mr. N. Vilakazi",
          title: "Principal",
          school: "Mlokothwa High School",
          site: "https://ikusasacombinedschool.site",
        },
        {
          text:
            "Parents used to complain about late communication — not anymore. BITSEMS delivers real-time messages and automated emails that keep everyone informed.",
          author: "Mr. Shandu",
          title: "SGB Chairperson",
          school: "Imbilane High School",
          site: "https://kwazulusecondary.site",
        },
        {
          text:
            "Our finance department now works smarter, not harder. BITSEMS makes it easy to track payments, print reports, and notify parents — all from one dashboard.",
          author: "Miss T. Dube",
          title: "Finance Officer",
          school: "Smiley Primary School",
          site: "https://thembelihlehigh.site",
        },
        {
          text:
            "Getting started with BITSEMS was quick and easy. Within days, we were sending fee reminders and managing our budget better than ever before.",
          author: "Mr. Thami Buthelezi",
          title: "Designated Educator for BITSEMS",
          school: "Kwa-Mame Primary School",
          site: "https://unitycollege.site",
        },
      ],

      features: [
        {
          icon: "zap",
          title: "AI Bulk SMS Campaigns",
          description:
            "Send targeted messages instantly using artificial intelligence.",
        },
        {
          icon: "file-text",
          title: "Fees & Statements",
          description:
            "Track fees, generate monthly statements, and manage accounts easily.",
        },
        {
          icon: "dollar-sign",
          title: "Budgets & Expenses",
          description:
            "Plan school finances and record expenses with transparency.",
        },
        {
          icon: "mail",
          title: "AI Email Campaigns",
          description:
            "Reach parents, teachers and stakeholders with AI-optimized emails.",
        },
        {
          icon: "users",
          title: "Manage Staff & SGB",
          description: "Keep accurate records of staff, roles, and governance.",
        },
        {
          icon: "book-open",
          title: "Books & Assets",
          description:
            "Catalog school textbooks, materials, and physical assets.",
        },
        {
          icon: "layout",
          title: "Online Portal",
          description:
            "Access for parents, staff, and learners with real-time updates.",
        },
        {
          icon: "check-circle",
          title: "Exam Results & SASAMS",
          description:
            "Publish results online and sync with SASAMS effortlessly.",
        },
      ],
      stats: [
        { number: "500+", label: "Schools Registered" },
        { number: "50,000+", label: "Messages Sent Daily" },
        { number: "1M+", label: "Records Managed" },
        { number: "99%", label: "Client Retention" },
      ],
      services: [
        {
          icon: "activity",
          title: "Smart SMS Tools",
          description:
            "Boost parent communication using AI-powered bulk SMS scheduling.",
        },
        {
          icon: "database",
          title: "Finance & Budgets",
          description:
            "Manage school budgets, termly billing, and monthly statements seamlessly.",
        },
        {
          icon: "globe",
          title: "Online Access",
          description:
            "Parent, staff and admin dashboards with mobile-friendly portals.",
        },
      ],
    };
  },
  mounted() {
    this.$nextTick(() => {
      window.lucide.createIcons();
    });
    this.interval = setInterval(this.nextSlide, 10000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  methods: {
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    prevSlide() {
      this.currentSlide =
        (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    },
    subscribe() {
      alert("Thank you for subscribing!");
      this.subscriberEmail = "";
    },
  },
};
</script>

<style scoped>
.slide-transition {
  transition: opacity 0.5s ease-in-out;
}
.feature-card:hover,
.testimonial-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}
.stat-number:hover {
  transform: scale(1.1);
}
</style>
